import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityCombobox } from '@/components/shared/entity-combobox';
import { FormDialog } from '@/components/shared/form-dialog';
import { FormField } from '@/components/shared/form-field';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { useChurchStore } from '@/stores/church-store';

import {
  WELFARE_REQUEST_TYPES,
  type WelfareRequest,
  type WelfareRequestType,
} from './welfare-types';

interface Member {
  id: string;
  firstName: string;
  lastName: string;
}

export const WelfareFormDialog = ({
  open,
  onOpenChange,
  editing,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: WelfareRequest | null;
  submitting: boolean;
  onSubmit: (values: {
    memberId: string;
    requestType: WelfareRequestType;
    description: string;
    requestedAmountMinor: number;
    currency: string;
    confidentialNotes: string | null;
    expectedUpdatedAt?: string;
  }) => void;
}) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const church = useChurchStore((state) => state.church);
  const currency = church?.baseCurrency || 'GHS';

  const [memberId, setMemberId] = useState<string | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberLabel, setMemberLabel] = useState('');
  const [requestType, setRequestType] = useState<WelfareRequestType>('other');
  const [description, setDescription] = useState('');
  const [amountMinor, setAmountMinor] = useState<number | null>(null);
  const [notes, setNotes] = useState('');

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', 'welfare', memberQuery],
    queryFn: () =>
      api.get<{ data: Member[] }>('/people/members', {
        query: { pageSize: 20, q: memberQuery.trim() || undefined },
      }),
    enabled: open,
    placeholderData: keepPreviousData,
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((row) => ({
        id: row.id,
        label: `${row.firstName} ${row.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setMemberId(editing.memberId);
      setMemberLabel(editing.memberName);
      setRequestType(
        WELFARE_REQUEST_TYPES.includes(editing.requestType as WelfareRequestType)
          ? (editing.requestType as WelfareRequestType)
          : 'other',
      );
      setDescription(editing.description);
      setAmountMinor(editing.requestedAmountMinor);
      setNotes(editing.confidentialNotes ?? '');
      return;
    }
    setMemberId(null);
    setMemberLabel('');
    setMemberQuery('');
    setRequestType('other');
    setDescription('');
    setAmountMinor(null);
    setNotes('');
  }, [open, editing]);

  const canSubmit = Boolean(memberId && description.trim() && amountMinor && amountMinor > 0);
  const selectedOption = memberId ? { id: memberId, label: memberLabel } : null;

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={editing ? t('welfare.form.editTitle') : t('welfare.form.createTitle')}
      description={editing ? t('welfare.form.editDescription') : t('welfare.form.createDescription')}
      size="lg"
      submitLabel={editing ? tCommon('action.save') : t('welfare.create')}
      cancelLabel={tCommon('action.cancel')}
      closeLabel={tCommon('action.close')}
      submitting={submitting}
      submitDisabled={!canSubmit}
      onSubmit={() => {
        if (!memberId || !amountMinor) return;
        onSubmit({
          memberId,
          requestType,
          description: description.trim(),
          requestedAmountMinor: amountMinor,
          currency,
          confidentialNotes: notes.trim() || null,
          expectedUpdatedAt: editing?.updatedAt,
        });
      }}
    >
      <FormField label={t('welfare.member')}>
        {(field) => (
          <EntityCombobox
            id={field.id}
            value={memberId}
            onChange={(id) => {
              setMemberId(id);
              const next = memberOptions.find((row) => row.id === id);
              setMemberLabel(next?.label ?? '');
            }}
            options={memberOptions}
            selected={selectedOption}
            onQueryChange={setMemberQuery}
            placeholder={t('welfare.memberPlaceholder')}
            loading={members.isLoading}
            popoverPortal="dialog"
            clearLabel={tCommon('action.clearSelection')}
          />
        )}
      </FormField>
      <FormField label={t('welfare.requestType')}>
        {(field) => (
          <Listbox
            id={field.id}
            value={requestType}
            onChange={(value) => setRequestType(value as WelfareRequestType)}
            options={WELFARE_REQUEST_TYPES.map((value) => ({
              value,
              label: t(`welfare.types.${value}`),
            }))}
            popoverPortal="dialog"
          />
        )}
      </FormField>
      <FormField label={t('welfare.amount')}>
        {(field) => (
            <MoneyInput
              id={field.id}
              currency={currency}
              value={amountMinor}
              onChange={setAmountMinor}
            />
        )}
      </FormField>
      <FormField label={t('welfare.descriptionLabel')}>
        {(field) => (
          <Textarea
            id={field.id}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('welfare.descriptionPlaceholder')}
            rows={3}
          />
        )}
      </FormField>
      <FormField label={t('welfare.confidentialNotes')} hint={t('welfare.confidentialNotesHint')}>
        {(field) => (
          <Textarea
            id={field.id}
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder={t('welfare.confidentialNotesPlaceholder')}
            rows={4}
          />
        )}
      </FormField>
    </FormDialog>
  );
};
